/**
 * The Pro Hub's COI form: what it asks for and when it may be sent (decision b0954f6f).
 *
 * Peter, 2026-10-05: "make it that she enters the new policy info, and just attach the doc." A pro
 * renewing her insurance types the NEW policy's details beside the certificate, so the filing carries
 * its own dates instead of inheriting last year's (the upload used to send only the file). The AI read
 * of the document is a check the owner sees, never a gate on the pro.
 */

export type CoiFormValues = {
  carrier: string;
  policyNumber: string;
  /** YYYY-MM-DD, or '' when not given. */
  effectiveDate: string;
  /** YYYY-MM-DD — required. */
  expirationDate: string;
  fileCount: number;
};

/**
 * HUB-056: the most files one filing carries. The receiving door (image-studios-backend
 * supabase/functions/pro-profile-update/index.ts, `fileUrls.length > 10` → 400 "Attach at most 10
 * files") refuses more, so the form refuses them BEFORE anything is uploaded — never after eleven
 * uploads that the filing then rejects. The pairing is pinned in image-os-web src/lib/coi-limit-parity.test.ts, whose CI
 * checks out this repo and the backend (this repo's CI cannot see the backend), and src/pages/CoiUpload.maxfiles.test.tsx
 * pins the number here. Change all three together. A press that fails after uploading takes its files back out
 * (HUB-056, CoiUpload.tsx).
 */
export const MAX_COI_FILES = 10;

/** Today as YYYY-MM-DD in the pro's own time zone (what a date input shows them). */
export function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** The first thing stopping the form from being sent, in plain words — or null when it can go. */
export function coiFormProblem(v: CoiFormValues, today: string = localToday()): string | null {
  if (!v.policyNumber.trim()) return 'Enter the policy number.';
  if (!v.expirationDate) return 'Enter the expiration date.';
  if (v.effectiveDate && v.expirationDate <= v.effectiveDate) {
    return 'The expiration date must be after the effective date.';
  }
  if (v.expirationDate <= today) {
    return 'That expiration date has passed. Enter the dates from your new policy.';
  }
  if (v.fileCount < 1) return 'Attach your certificate (PDF or photo).';
  if (v.fileCount > MAX_COI_FILES) return `Attach at most ${MAX_COI_FILES} files — remove ${v.fileCount - MAX_COI_FILES} to send.`;
  return null;
}

/** Only PDFs and images are a certificate. */
export function isCertificateFile(f: { type: string }): boolean {
  return f.type === 'application/pdf' || f.type.startsWith('image/');
}
